import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Text from 'components/_UI/Text';
import Button from 'components/_UI/Button';
import { Row } from 'styled/alignment/Row';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { useEventFilter } from 'context/events/EventFilter';
import { useUserLocation } from 'context/user/UserLocation';
import tagsApi from 'api/requests/tags';
import { transformedDateFilters } from 'theme/filters';
import { transformedEventTypes } from 'theme/eventTypes';
import { MapPin, Calendar, X } from 'lucide-react';
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap';

const MIN_RANGE = 1;
const MAX_RANGE = 50;
const DEFAULT_RANGE = 25;

export default function FiltersModal({ isOpen, onClose }) {
    const { eventFilter, setEventFilter } = useEventFilter();
    const { userLocation } = useUserLocation();

    const [distance, setDistance] = useState(eventFilter?.distance_range || DEFAULT_RANGE);
    const [date, setDate] = useState(eventFilter?.dates || null);
    const [eventType, setEventType] = useState(eventFilter?.classification_id || null);
    const [selectedCategories, setSelectedCategories] = useState(eventFilter?.categories || []);
    const [tags, setTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchTags();
            setDistance(eventFilter?.distance_range || DEFAULT_RANGE);
            setDate(eventFilter?.dates || null);
            setEventType(eventFilter?.classification_id || null);
            setSelectedCategories(eventFilter?.categories || []);
        }
    }, [isOpen]);

    async function fetchTags() {
        setLoadingTags(true);
        try {
            const { data } = await tagsApi.get(1);
            setTags(data || []);
        } catch (error) {
            console.error('Error fetching tags:', error);
        } finally {
            setLoadingTags(false);
        }
    }

    function toggleCategory(tagName) {
        setSelectedCategories(prev =>
            prev.includes(tagName)
                ? prev.filter(c => c !== tagName)
                : [...prev, tagName]
        );
    }

    function handleClear() {
        setDistance(DEFAULT_RANGE);
        setDate(null);
        setEventType(null);
        setSelectedCategories([]);
        setEventFilter(null);
        onClose();
    }

    const hasLocalFilters = !!(date || eventType || selectedCategories.length > 0 ||
        (userLocation?.lat && distance !== DEFAULT_RANGE));

    function handleApply() {
        const hasFilters = hasLocalFilters;

        setEventFilter(hasFilters ? {
            distance_range: userLocation?.lat ? distance : null,
            dates: date,
            classification_id: eventType,
            categories: selectedCategories,
        } : null);

        onClose();
    }

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose}>
                    <X size={18} color={colors.nightDark} />
                </CloseButton>

                <ScrollArea>
                    {(userLocation?.lat && userLocation?.long) && (
                        <Section>
                            <SectionHeader>
                                <Row verticalCenter width='auto'>
                                    <MapPin size={18} color={colors.secondaryPurple} />
                                    <Text
                                        name='highlight'
                                        type='bold'
                                        color={colors.secondaryPurple}
                                        paddingBottom={0}
                                        marginLeft='8px'
                                    >
                                        Distância
                                    </Text>
                                </Row>
                                <Text paddingBottom={0} color={colors.secondaryPurple} type='bold'>
                                    {distance}km
                                </Text>
                            </SectionHeader>
                            <Text name='small' color={colors.nightDark}>
                                Escolha uma distância personalizada
                            </Text>
                            <StyledRange
                                type='range'
                                min={MIN_RANGE}
                                max={MAX_RANGE}
                                value={distance}
                                onChange={(e) => setDistance(Number(e.target.value))}
                            />
                        </Section>
                    )}

                    <Section>
                        <Row verticalCenter width='auto' marginBottom='4px'>
                            <Calendar size={18} color={colors.secondaryPurple} />
                            <Text
                                name='highlight'
                                type='bold'
                                color={colors.secondaryPurple}
                                paddingBottom={0}
                                marginLeft='8px'
                            >
                                Data
                            </Text>
                        </Row>
                        <Text name='small' color={colors.nightDark}>
                            Escolha o período que deseja buscar.
                        </Text>
                        <StyledSelect
                            value={date || ''}
                            onChange={(e) => setDate(e.target.value || null)}
                        >
                            <option value=''>Selecione um período</option>
                            {transformedDateFilters.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </StyledSelect>
                    </Section>

                    <Section>
                        <Text
                            name='highlight'
                            type='bold'
                            color={colors.secondaryPurple}
                            paddingBottom={0}
                            marginBottom='4px'
                        >
                            Tipo de evento
                        </Text>
                        <Text name='small' color={colors.nightDark}>
                            Escolha os tipos de evento que deseja buscar.
                        </Text>
                        <StyledSelect
                            value={eventType || ''}
                            onChange={(e) => setEventType(e.target.value || null)}
                        >
                            <option value=''>Selecione um tipo de evento</option>
                            {transformedEventTypes.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </StyledSelect>
                    </Section>

                    <Section>
                        <Text
                            name='highlight'
                            type='bold'
                            color={colors.secondaryPurple}
                            paddingBottom={0}
                            marginBottom='4px'
                        >
                            Categoria
                        </Text>
                        <Text name='small' color={colors.nightDark}>
                            Escolha uma categoria
                        </Text>
                        <CategoriesGrid>
                            {loadingTags ? (
                                <Text name='small' color={colors.nightLight}>Carregando...</Text>
                            ) : (
                                tags.map(tag => {
                                    const isSelected = selectedCategories.includes(tag.name);
                                    const TagIcon = getCategoryIcon(tag.icon);
                                    return (
                                        <CategoryChip
                                            key={tag.id}
                                            selected={isSelected}
                                            onClick={() => toggleCategory(tag.name)}
                                        >
                                            <TagIcon size={14} color={isSelected ? colors.neutralLight : colors.secondaryPurple} />
                                            <Text
                                                name='small'
                                                paddingBottom={0}
                                                color={isSelected ? colors.neutralLight : colors.secondaryPurple}
                                            >
                                                {tag.name}
                                            </Text>
                                        </CategoryChip>
                                    );
                                })
                            )}
                        </CategoriesGrid>
                    </Section>
                </ScrollArea>

                <ModalFooter>
                    <Column width='48%'>
                        <Button
                            category='roundedBorded'
                            variant={!hasLocalFilters ? 'disabled' : undefined}
                            marginBottom={0}
                            onClick={handleClear}
                        >
                            Limpar
                        </Button>
                    </Column>
                    <Column width='48%'>
                        <Button variant='featured' marginBottom={0} onClick={handleApply}>
                            Aplicar Filtros
                        </Button>
                    </Column>
                </ModalFooter>
            </ModalCard>
        </Overlay>
    );
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(26, 26, 26, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 16px;
`;

const ModalCard = styled.div`
    position: relative;
    background: ${colors.neutralLight};
    border-radius: 12px;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 700px;
        max-height: 85vh;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.nightLight};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1;
    transition: background 0.2s ease;

    &:hover {
        background: ${colors.neutralDark};
    }
`;

const ScrollArea = styled(Column)`
    overflow-y: auto;
    flex: 1;
    padding: 40px 28px 16px 28px;
    align-items: flex-start;
`;

const Section = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-bottom: 24px;
`;

const SectionHeader = styled(Row)`
    width: 100%;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
`;

const StyledRange = styled.input`
    width: 100%;
    margin-top: 12px;
    accent-color: ${colors.secondaryPurple};
    height: 6px;
    cursor: pointer;
`;

const StyledSelect = styled.select`
    width: 100%;
    margin-top: 10px;
    padding: 14px 16px;
    border: 1px solid ${colors.nightLight};
    border-radius: 8px;
    appearance: none;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") no-repeat right 14px center;
    font-size: 14px;
    color: ${colors.nightDark};
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: ${colors.secondaryPurple};
    }
`;

const CategoriesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
    width: 100%;
`;

const CategoryChip = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: 8px;
    border: 1px solid ${colors.secondaryPurple};
    background: ${({ selected }) => selected ? colors.secondaryPurple : 'transparent'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ selected }) => selected ? colors.secondaryPurple : colors.occasionalPurple};
    }
`;

const ModalFooter = styled(Row)`
    padding: 16px 28px;
    border-top: 1px solid ${colors.nightLight};
    justify-content: space-between;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;
